import {useCallback} from "react";
import {create} from "zustand";

export interface CacheValueFn<D extends Record<string, any>> {
    (): D; // no args, return entire cache
    <T extends keyof D>(field: T): D[T] | undefined; // no default
    <T extends keyof D>(field: T, def: D[T]): D[T]; // with default
}

export type ComponentCacheHook<D extends Record<string, any>> = {
    cacheReset: () => void;
    cacheValue: CacheValueFn<D>;
    cacheDump: () => D;
    cacheSave: (saveObject: D) => void;
    cacheSetField: <T>(field: string & keyof D, value: T) => void;
}

// Zustand store for component cache
// Stores arbitrary data keyed by component ID with no restrictions on data types
interface ComponentCacheStore {
    items: Record<string, any>;
    hasKey: (key: string) => boolean;
    setItem: (key: string, value: any) => void;
    setField: (key: string, field: string, value: any) => void;
    resetItem: (key: string) => void;
    getItem: (key: string) => any;
}

const BUILTIN_VALUE_TYPES: Function[] = [Date, Error, Map, Set, RegExp, Promise];

/**
 * True for a value that holds domain data rather than UI state: an object that
 * carries the `Base` write stamps (`addedOn` / `dbAddedOn`), or an instance of an
 * application class (PaymentCreditCard, CPayrixApplication and other non-`Base`
 * domain objects). Arrays, plain objects without stamps, and built-in value types
 * (Date, Error, Map, Set) are not.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isCachedDomainEntity(value: unknown): boolean {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
    if ('addedOn' in value || 'dbAddedOn' in value) return true;
    const proto = Object.getPrototypeOf(value);
    if (proto === Object.prototype || proto === null) return false;
    return !BUILTIN_VALUE_TYPES.some(t => value instanceof t);
}

/**
 * OWA-526 gate-write, applied to every write into the store (the hook's
 * `cacheSave`/`cacheSetField` and `componentCacheStore.setItem`/`setField`/`typed`).
 *
 * A record that declares itself clean (`hasChanged === false`) and holds domain
 * data is a snapshot of an editor with no unsaved work. Storing it would restore
 * that snapshot over fresh Redux state on the next mount (May 2026 Lawson invoice;
 * the OWA-687 lost-update sequence, where a later save writes the stale copy back
 * over a colleague's newer one), so it is not stored at all. Everything else in
 * such a record (expanded flags, fields derived from the entity such as a user's
 * roles) was read from the same stale snapshot, which is why the whole record goes
 * rather than only the entity field.
 *
 * Domain data means a `current` field holding any object (the editor convention,
 * which also covers plain-object editors such as hourly rates and hard costs), or
 * any top-level field that `isCachedDomainEntity` accepts.
 *
 * Records with `hasChanged: true` (an unsaved draft) and records without a
 * `hasChanged` field (transient UI state) are stored unchanged.
 *
 * @param {unknown} value - the record about to be stored
 * @returns {boolean} true when the record must not be stored
 */
export function isCleanEntityRecord(value: unknown): boolean {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
    const record = value as Record<string, unknown>;
    if (record.hasChanged !== false) return false;
    if (typeof record.current === 'object' && record.current !== null) return true;
    return Object.values(record).some(isCachedDomainEntity);
}

const withoutKey = (items: Record<string, any>, key: string): Record<string, any> => {
    const {[key]: _, ...rest} = items;
    return rest;
};

const useComponentCacheStore = create<ComponentCacheStore>((set, get) => ({
    items: {},
    hasKey: (key: string) => {
        return get().items[key] !== undefined;
    },
    setItem: (key: string, value: any) => {
        set((state) => ({
            items: isCleanEntityRecord(value)
                   ? withoutKey(state.items, key)
                   : {...state.items, [key]: value}
        }));
    },
    setField: (key: string, field: string, value: any) => {
        set((state) => {
            const merged = {...state.items[key], [field]: value};
            return {
                items: isCleanEntityRecord(merged)
                       ? withoutKey(state.items, key)
                       : {...state.items, [key]: merged}
            };
        });
    },
    resetItem: (key: string) => {
        set((state) => {
            const {[key]: _, ...rest} = state.items;
            return {items: rest};
        });
    },
    getItem: (key: string) => {
        return get().items[key];
    },
}));

/**
 * Hook for caching component state across mount/unmount cycles.
 *
 * Storage: in-memory Zustand store. Data is per-tab, lost on hard refresh
 * and on closing the tab. There is no persistence layer (no localStorage,
 * no sessionStorage, no IndexedDB).
 *
 * Unlike Redux-based storage, Zustand allows storing any data type including:
 * - Class instances with methods
 * - Objects with circular references
 * - React elements
 * - Functions
 * - Complex nested structures
 *
 * # What to cache
 *
 * The cache's **primary purpose** is to preserve the user's unsaved mid-edit
 * changes across remounts (so navigating away and back doesn't lose work).
 *
 * Cache:
 *  - Transient UI state that should survive a remount inside the same tab:
 *    scroll position, expanded sections, filter inputs, autofocus-already-
 *    ran flags.
 *  - The user's draft of a domain entity WHEN they have unsaved edits
 *    (gated on `hasChanged === true` or equivalent).
 *
 * Do NOT cache:
 *  - **Clean (unmodified) domain entities.** When `hasChanged === false`,
 *    leave `current: Entity` out of the cache so the next mount reads fresh
 *    from Redux. The store enforces this for any record that carries
 *    `hasChanged` (see `isCleanEntityRecord`, OWA-526). Otherwise the cached snapshot will diverge from server
 *    state when other tabs / Socket.IO sync the entity. (May 2026 Lawson
 *    Legal bug: cached InvoiceCard showed three different totals over 60h
 *    on the same DB state.)
 *  - **Transient UI state owned by a controlled child** like `isMenuOpen`.
 *    Caching it overrides the child's intent on the next mount. (Dec 2024
 *    Select dropdown bug — cached `isMenuOpen: false` immediately closed
 *    menus the user had just opened.)
 *  - Recomputable values (totals, summaries) that depend on data outside
 *    the cache key — recompute on render instead.
 *
 * # API semantics (read these once, then forget them — they're surprising)
 *
 *  - `cacheSave(D)` — **OVERWRITES** the entire cache entry. If you save
 *    `{a:1}` and later `cacheSave({b:2})`, `a` is gone. Use for "save the
 *    whole state on unmount" patterns.
 *  - `cacheSetField(field, value)` — **MERGES** one field into the entry.
 *    `cacheSetField('a', 1)` then `cacheSetField('b', 2)` leaves both
 *    `{a:1, b:2}` in the entry.
 *  - `cacheValue()` — returns the whole entry (or `undefined`).
 *  - `cacheValue(field)` — returns the field value or `undefined`.
 *  - `cacheValue(field, default)` — returns the field value if the field is
 *    present in the entry, else the default. Note: explicitly storing
 *    `undefined` as a field value will return `undefined` from this call,
 *    NOT the default. `in` is the existence check, not value-truthiness.
 *  - `cacheReset()` — deletes the entry entirely.
 *
 * # Keys
 *
 * Cache keys are flat strings; collisions cause data leakage between
 * components. Conventions in use today:
 *  - Functional components: `\`component-name-${id}\`` (kebab-case)
 *  - Class components: `\`ComponentName_${id}\`` (PascalCase + underscore)
 * Stick with one or the other within a single component. The
 * `useComponentCache.keys.test.ts` spec asserts no collisions across files.
 *
 * @example
 *
 *  const {cacheSave, cacheReset, cacheValue} = useComponentCache<TaskState>(`task-item-${id}`);
 *
 *  // Initialize state from cache (or default).
 *  const [task, setTask] = useState(cacheValue('task', null));
 *
 *  // Save the whole state on unmount.
 *  useEffect(() => {
 *      return () => cacheSave({task});
 *  }, [cacheSave, task]);
 *
 *  // Clear cache on OK/Cancel so the next mount starts fresh.
 *  const onOk = () => { cacheReset(); props.onOk?.(); };
 *  const onCancel = () => { cacheReset(); props.onCancel?.(); };
 *
 * @param {string} key - unique key for this cache entry
 * @returns {ComponentCacheHook}
 */
function useComponentCache<D extends object>(key: string): ComponentCacheHook<D> {
    // Get the Zustand store actions
    const setItem = useComponentCacheStore(state => state.setItem);
    const setField = useComponentCacheStore(state => state.setField);
    const resetItem = useComponentCacheStore(state => state.resetItem);
    const getItem = useComponentCacheStore(state => state.getItem);

    // Get the current cached value for this key
    const componentCache = useComponentCacheStore(state => state.items[key]) as D | undefined;

    // Save the entire record (all items)
    const cacheSave = useCallback((item: D) => {
        // Store directly without any cloning or equality checks
        // Zustand handles updates efficiently
        setItem(key, item);
    }, [key, setItem]);

    const cacheSetField = useCallback(<T,>(field: string & keyof D, value: T) => {
        setField(key, field, value);
    }, [key, setField]);

    const cacheReset = useCallback(() => {
        resetItem(key);
    }, [key, resetItem]);

    const cacheDump = useCallback((): D => {
        return getItem(key) as D;
    }, [getItem, key]);

    /**
     * Get the value from the cache.
     *
     * If a field is provided, return the value for that field.
     * If a default value is provided, and the cache does not contain the requested field, return the default value.
     * If no field or default is provided, return the entire cache.
     *
     * @param {string} [field]
     * @param {any} [def]
     */
    const cacheValue: CacheValueFn<D> = useCallback((field?: any, def?: any) => {
        // If no field provided, returns the entire cache.
        // Use `field === undefined` (not `!field`) so that legitimate
        // falsy field names (e.g. '' or 0) still take the lookup path.
        if (field === undefined) return cacheDump();

        // The cache has been created and contains the requested field
        if (componentCache && (field in componentCache)) {
            return componentCache[field as keyof D];
        }

        // The cache does not contain the requested field but default value provided
        if (def !== undefined) return def;

        // No field found and no default provided
        return undefined;
    }, [cacheDump, componentCache]) as CacheValueFn<D>;

    return {cacheSave, cacheReset, cacheValue, cacheDump, cacheSetField};
}

export default useComponentCache;

/**
 * Direct access to component cache store for class components
 * Use these methods when you can't use the hook (e.g., in class components)
 * 
 * @example
 * interface MyState {
 *   taskId: string;
 *   hasChanged: boolean;
 * }
 * 
 * class MyComponent extends React.Component {
 *   private cache = componentCacheStore.typed<MyState>('myKey');
 * 
 *   componentDidMount() {
 *     if (this.cache.hasKey()) {
 *       const state = this.cache.getItem();
 *       this.setState({...state});
 *     }
 *   }
 * 
 *   componentWillUnmount() {
 *     this.cache.setItem(this.state);
 *   }
 * }
 */
// Expose a global function for E2E tests to clear the entire component cache.
// This prevents stale cached editor state from interfering with deep link navigation.
if (typeof window !== 'undefined') {
    (window as any).__clearComponentCache = () => {
        useComponentCacheStore.setState({items: {}});
    };
}

export const componentCacheStore = {
    hasKey: (key: string): boolean => {
        return useComponentCacheStore.getState().hasKey(key);
    },
    setItem: (key: string, value: any) => {
        useComponentCacheStore.getState().setItem(key, value);
    },
    setField: (key: string, field: string, value: any) => {
        useComponentCacheStore.getState().setField(key, field, value);
    },
    resetItem: (key: string) => {
        useComponentCacheStore.getState().resetItem(key);
    },
    getItem: (key: string): any => {
        return useComponentCacheStore.getState().getItem(key);
    },
    /**
     * Tests only: store a record WITHOUT the clean-entity gate, to reproduce an
     * entry written before the gate existed (testEntityCacheInvariants seeds a
     * clean stale snapshot this way so invariant A cannot pass vacuously).
     */
    seedUncheckedForTest: (key: string, value: any) => {
        useComponentCacheStore.setState(state => ({items: {...state.items, [key]: value}}));
    },
    /**
     * Every cached entry, keyed by cache key. Read-only snapshot for callers
     * that need to scan across components (OWA-827: the update gate looks for
     * any entry carrying unsaved edits before a reload).
     */
    items: (): Record<string, any> => {
        return useComponentCacheStore.getState().items;
    },
    
    /**
     * Create a typed interface for the cache store with a specific key
     * Call this once in your component to get type-safe access
     */
    typed: <D extends Record<string, any>>(key: string) => ({
        exists: (): boolean => {
            return useComponentCacheStore.getState().hasKey(key);
        },
        setItem: (value: D) => {
            useComponentCacheStore.getState().setItem(key, value);
        },
        setField: <T extends keyof D>(field: string & T, value: D[T]) => {
            useComponentCacheStore.getState().setField(key, field, value);
        },
        resetItem: () => {
            useComponentCacheStore.getState().resetItem(key);
        },
        getItem: (): D | undefined => {
            return useComponentCacheStore.getState().getItem(key) as D | undefined;
        },
        getField: <T extends keyof D>(field: string & T): D[T] | undefined => {
            const item = useComponentCacheStore.getState().getItem(key) as D | undefined;
            return item?.[field];
        },
        getFieldWithDefault: <T extends keyof D>(field: string & T, defaultValue: D[T]): D[T] => {
            const item = useComponentCacheStore.getState().getItem(key) as D | undefined;
            return item?.[field] ?? defaultValue;
        }
    })
};
